import React, { useCallback, useEffect, useState } from 'react'
import {
  Button, Form, Input, InputNumber, Modal, Popconfirm, Select, Space, Table, Tag, Typography, message,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { del, get, patch, post } from '../../lib/request'
import { UNIT_STATUS_LABELS, Unit, errMsg, fmtMoney } from '../../lib/api'
import { useAuthStore } from '../../stores/auth'
import { hasPermission } from '../../utils/permissions'

const { Text } = Typography

/** 楼盘字典 CRUD（通用资产模型，供未来直租复用） */
const UnitTab: React.FC = () => {
  const role = useAuthStore((s) => s.user?.role)
  const canEdit = hasPermission(role, 'UNIT_EDIT')

  const [list, setList] = useState<Unit[]>([])
  const [loading, setLoading] = useState(false)
  const [status, setStatus] = useState<string | undefined>(undefined)
  const [keyword, setKeyword] = useState('')
  const [editing, setEditing] = useState<Unit | 'new' | null>(null)
  const [form] = Form.useForm()
  const [saving, setSaving] = useState(false)

  const fetchList = useCallback(() => {
    setLoading(true)
    get<Unit[]>('/admin/units', {
      params: { ...(status ? { status } : {}), ...(keyword ? { keyword } : {}) },
    })
      .then((d) => setList(Array.isArray(d) ? d : []))
      .catch((err) => message.warning(errMsg(err, '楼盘字典加载失败')))
      .finally(() => setLoading(false))
  }, [status, keyword])

  useEffect(() => { fetchList() }, [fetchList])

  const openEdit = (u?: Unit) => {
    if (u) {
      setEditing(u)
      form.setFieldsValue({ ...u })
    } else {
      setEditing('new')
      form.resetFields()
    }
  }

  const save = async () => {
    const v = await form.validateFields()
    setSaving(true)
    try {
      if (editing === 'new') {
        await post('/admin/units', v)
        message.success('单元已新增')
      } else if (editing) {
        await patch(`/admin/units/${editing.id}`, v)
        message.success('单元已更新')
      }
      setEditing(null)
      fetchList()
    } catch (err: unknown) {
      message.error(errMsg(err, '保存失败'))
    } finally {
      setSaving(false)
    }
  }

  const columns: ColumnsType<Unit> = [
    { title: 'ID', dataIndex: 'id', key: 'id', render: (v: number) => `#${v}` },
    { title: '楼栋', dataIndex: 'building', key: 'building' },
    { title: '楼层', dataIndex: 'floor', key: 'floor' },
    { title: '单元号', dataIndex: 'unitNo', key: 'unitNo' },
    { title: '面积（㎡）', dataIndex: 'area', key: 'area' },
    {
      title: '租金区间（元/㎡/月）',
      key: 'rent',
      render: (_: unknown, r) =>
        r.rentPriceMin != null || r.rentPriceMax != null
          ? `${r.rentPriceMin ?? '—'} ~ ${r.rentPriceMax ?? '—'}`
          : '—',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (v: string) => (
        <Tag color={UNIT_STATUS_LABELS[v]?.color ?? 'default'}>{UNIT_STATUS_LABELS[v]?.label ?? v}</Tag>
      ),
    },
    ...(canEdit
      ? [{
          title: '操作',
          key: 'actions',
          fixed: 'right' as const,
          render: (_: unknown, r: Unit) => (
            <Space size={4}>
              <Button size="small" style={{ minHeight: 32 }} onClick={() => openEdit(r)}>修改</Button>
              <Popconfirm
                title="删除该单元？"
                description="已有成交关联的单元不可删除（后端校验）"
                okText="删除"
                cancelText="取消"
                onConfirm={() =>
                  del(`/admin/units/${r.id}`)
                    .then(() => { message.success('已删除'); fetchList() })
                    .catch((err: unknown) => message.error(errMsg(err, '删除失败')))
                }
              >
                <Button size="small" danger style={{ minHeight: 32 }}>删除</Button>
              </Popconfirm>
            </Space>
          ),
        }]
      : []),
  ]

  return (
    <>
      <Space style={{ marginBottom: 14 }} wrap>
        <Select
          allowClear
          placeholder="全部状态"
          style={{ minWidth: 120 }}
          value={status}
          onChange={setStatus}
          options={Object.entries(UNIT_STATUS_LABELS).map(([value, s]) => ({ value, label: s.label }))}
        />
        <Input.Search
          allowClear
          placeholder="搜索楼栋 / 楼层 / 单元号"
          style={{ width: 240 }}
          onSearch={setKeyword}
        />
        {canEdit && (
          <Button type="primary" onClick={() => openEdit()} style={{ minHeight: 44 }}>
            新增单元
          </Button>
        )}
      </Space>
      <Table<Unit>
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={list}
        scroll={{ x: 'max-content' }}
        style={{ background: '#fff', borderRadius: 12 }}
        pagination={{ pageSize: 20, showTotal: (t) => `共 ${t} 条` }}
      />
      <Modal
        title={editing === 'new' ? '新增单元' : '修改单元'}
        open={!!editing}
        onOk={save}
        onCancel={() => setEditing(null)}
        okText="保存"
        cancelText="取消"
        confirmLoading={saving}
        destroyOnClose
      >
        <Form form={form} layout="vertical" initialValues={{ status: 'available' }}>
          <Form.Item name="building" label="楼栋" rules={[{ required: true, message: '楼栋必填' }]}>
            <Input placeholder="例：T1" />
          </Form.Item>
          <Form.Item name="floor" label="楼层" rules={[{ required: true, message: '楼层必填' }]}>
            <Input placeholder="例：12" />
          </Form.Item>
          <Form.Item name="unitNo" label="单元号" rules={[{ required: true, message: '单元号必填' }]}>
            <Input placeholder="例：1201" />
          </Form.Item>
          <Form.Item name="area" label="面积（㎡）" rules={[{ required: true, message: '面积必填' }]}>
            <InputNumber min={0} style={{ width: '100%', minHeight: 44 }} />
          </Form.Item>
          <Space.Compact block>
            <Form.Item name="rentPriceMin" label="租金下限（元/㎡/月）" style={{ width: '50%', marginRight: 8 }}>
              <InputNumber min={0} style={{ width: '100%', minHeight: 44 }} />
            </Form.Item>
            <Form.Item name="rentPriceMax" label="租金上限（元/㎡/月）" style={{ width: '50%' }}>
              <InputNumber min={0} style={{ width: '100%', minHeight: 44 }} />
            </Form.Item>
          </Space.Compact>
          <Form.Item name="status" label="状态">
            <Select
              options={Object.entries(UNIT_STATUS_LABELS).map(([value, s]) => ({ value, label: s.label }))}
            />
          </Form.Item>
        </Form>
        <Text type="secondary" style={{ fontSize: 12 }}>
          成交录入时面积 × 单价自动生成首月租金（{fmtMoney(null)} 栏位由 DB 计算）
        </Text>
      </Modal>
    </>
  )
}

export default UnitTab
